"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import useSWR from "swr";
import {
  resumeBenchmarkService,
  getResumeBenchmarkDetails,
} from "@/_services/resumeServices/benchmark";
import { usePdfToImageConverter } from "@/_components/ui/UploadResumePanel/_hooks/usePdfToImageConverter";
import { ResumeStatus } from "@/_models/resume";
import type { ResumeBenchmarkDetailResponse } from "@/_models/resume-benchmark";
import { useFingerprint } from "@/_hooks/useFingerprint";
import {
  BIEvents,
  trackEvent,
} from "@/_components/lib/mixpanel/mixpanelClient";
import { useLocale } from "@/_hooks/useLocale";

// ─── Stage ───────────────────────────────────────────────────────────────────

export enum BenchmarkStage {
  UPLOAD = "upload",
  LOADING = "loading",
  RESULT = "result",
  ERROR = "error",
}

function parseStageFromUrl(
  stageParam: string | null,
  resumeIdParam: string | null,
): BenchmarkStage {
  if (!resumeIdParam) {
    if (stageParam === BenchmarkStage.ERROR) return BenchmarkStage.ERROR;
    return BenchmarkStage.UPLOAD;
  }
  if (stageParam === BenchmarkStage.RESULT) return BenchmarkStage.RESULT;
  if (stageParam === BenchmarkStage.LOADING) return BenchmarkStage.LOADING;
  if (stageParam === BenchmarkStage.ERROR) return BenchmarkStage.ERROR;
  return BenchmarkStage.UPLOAD;
}

// ─── Progress constants ───────────────────────────────────────────────────────

const PROGRESS_INTERVAL_MS = 750;
const PROGRESS_INCREMENT = 1.5; // ~1.5% per tick → reaches 90% in ~45s
const PROGRESS_SOFT_CAP = 97;

// ─── Context type ─────────────────────────────────────────────────────────────

interface BenchmarkContextType {
  stage: BenchmarkStage;
  file: File | null;
  resumeId: string | null;
  benchmarkDetails: ResumeBenchmarkDetailResponse | null;
  progress: number;
  isStarting: boolean;
  startBenchmark: (file: File) => Promise<void>;
  reset: () => void;
}

const BenchmarkContext = createContext<BenchmarkContextType | undefined>(
  undefined,
);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function BenchmarkProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { convertPdfToImage } = usePdfToImageConverter();
  const { visitorId } = useFingerprint();

  const locale = useLocale() as "en" | "vi";
  const urlResumeId = searchParams.get("resumeId");
  const urlStage = parseStageFromUrl(searchParams.get("stage"), urlResumeId);

  const [stage, setStage] = useState<BenchmarkStage>(urlStage);
  const [file, setFile] = useState<File | null>(null);
  const [resumeId, setResumeId] = useState<string | null>(urlResumeId);
  const [benchmarkDetails, setBenchmarkDetails] =
    useState<ResumeBenchmarkDetailResponse | null>(null);
  const [progress, setProgress] = useState(
    urlStage === BenchmarkStage.RESULT ? 100 : 0,
  );
  const [isStarting, setIsStarting] = useState(false);

  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const visitorIdRef = useRef(visitorId);
  const resumeIdRef = useRef<string | null>(urlResumeId);
  useEffect(() => {
    visitorIdRef.current = visitorId;
  }, [visitorId]);
  useEffect(() => {
    resumeIdRef.current = resumeId;
  }, [resumeId]);

  // ─── Progress interval — runs while LOADING, stops on any other stage ───────

  useEffect(() => {
    if (stage === BenchmarkStage.LOADING) {
      setProgress(0);
      progressIntervalRef.current = setInterval(() => {
        setProgress((p) => Math.min(p + PROGRESS_INCREMENT, PROGRESS_SOFT_CAP));
      }, PROGRESS_INTERVAL_MS);
    } else {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    }
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [stage]);

  // ─── URL sync ─────────────────────────────────────────────────────────────

  const pushStageToUrl = useCallback(
    (newStage: BenchmarkStage, nextResumeId?: string | null) => {
      const params = new URLSearchParams();
      const id =
        nextResumeId !== undefined ? nextResumeId : resumeIdRef.current;

      if (newStage !== BenchmarkStage.UPLOAD) {
        params.set("stage", newStage);
        if (id) params.set("resumeId", id);
      }

      const query = params.toString();
      router.replace(query ? `${pathname}?${query}` : pathname, {
        scroll: false,
      });
    },
    [router, pathname],
  );

  // ─── Restore RESULT details after remount (e.g. language switch) ───────────

  const shouldFetchDetails =
    stage === BenchmarkStage.RESULT &&
    Boolean(resumeId) &&
    !benchmarkDetails &&
    Boolean(visitorId);

  const { data: restoredDetails, error: restoreError } = useSWR(
    shouldFetchDetails
      ? `/resume-benchmark/detail?resumeId=${resumeId}&fingerprintId=${visitorId}`
      : null,
    () => getResumeBenchmarkDetails(resumeId!, visitorId!),
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  useEffect(() => {
    if (!restoredDetails) return;
    setBenchmarkDetails(restoredDetails);
    setProgress(100);
  }, [restoredDetails]);

  useEffect(() => {
    if (!restoreError) return;
    setStage(BenchmarkStage.ERROR);
    pushStageToUrl(BenchmarkStage.ERROR);
  }, [restoreError, pushStageToUrl]);

  // ─── Polling — active only while LOADING and resumeId is known ─────────────

  const shouldPollStatus =
    stage === BenchmarkStage.LOADING && Boolean(resumeId);

  const { data: statusData } = useSWR(
    shouldPollStatus ? `/resume-benchmark/status?resumeId=${resumeId}` : null,
    () => resumeBenchmarkService.getResumeBenchmarkStatus(resumeIdRef.current!),
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
      refreshInterval(latestData) {
        if (!latestData) return 5000;
        if (
          latestData.status === ResumeStatus.FINISH_ANALYZING ||
          latestData.status === ResumeStatus.FAILED
        ) {
          return 0;
        }
        return 5000;
      },
    },
  );

  // ─── React to terminal polling status ──────────────────────────────────────

  useEffect(() => {
    if (!statusData) return;

    if (statusData.status === ResumeStatus.FINISH_ANALYZING) {
      setProgress(100);
      getResumeBenchmarkDetails(statusData.id, visitorIdRef.current)
        .then((details) => {
          setBenchmarkDetails(details);
          setStage(BenchmarkStage.RESULT);
          pushStageToUrl(BenchmarkStage.RESULT, statusData.id);
          trackEvent(BIEvents.BENCHMARK_RESULT, {
            resume_id: statusData.id,
          });
        })
        .catch(() => {
          setStage(BenchmarkStage.ERROR);
          pushStageToUrl(BenchmarkStage.ERROR, statusData.id);
          trackEvent(BIEvents.BENCHMARK_ERROR, {
            resume_id: statusData.id,
            stage: "details_fetch",
          });
        });
    }

    if (statusData.status === ResumeStatus.FAILED) {
      setStage(BenchmarkStage.ERROR);
      pushStageToUrl(BenchmarkStage.ERROR, statusData.id);
      trackEvent(BIEvents.BENCHMARK_ERROR, {
        resume_id: statusData.id,
        stage: "analysis_failed",
      });
    }
  }, [statusData, pushStageToUrl]);

  // ─── Actions ──────────────────────────────────────────────────────────────

  const startBenchmark = useCallback(
    async (selectedFile: File) => {
      if (!visitorIdRef.current) {
        console.error("[Benchmark] fingerprint not ready yet, aborting");
        return;
      }
      setIsStarting(true);
      setFile(selectedFile);
      setBenchmarkDetails(null);
      setStage(BenchmarkStage.LOADING);
      pushStageToUrl(BenchmarkStage.LOADING, null);
      try {
        const thumbnailFile = await convertPdfToImage(selectedFile, {
          isThumbnail: true,
        });
        const { resume_id } = await resumeBenchmarkService.startResumeBenchmark(
          visitorIdRef.current,
          selectedFile,
          thumbnailFile,
          locale,
        );
        if (!resume_id) {
          throw new Error("Missing resume id after benchmark start");
        }
        resumeIdRef.current = resume_id;
        setResumeId(resume_id);
        pushStageToUrl(BenchmarkStage.LOADING, resume_id);
        trackEvent(BIEvents.BENCHMARK_START, {
          resume_id,
        });
      } catch (error) {
        console.error("[Benchmark] startBenchmark failed:", error);
        setStage(BenchmarkStage.ERROR);
        pushStageToUrl(BenchmarkStage.ERROR);
        trackEvent(BIEvents.BENCHMARK_ERROR, {
          stage: "start",
        });
      } finally {
        setIsStarting(false);
      }
    },
    [convertPdfToImage, pushStageToUrl],
  );

  const reset = useCallback(() => {
    setStage(BenchmarkStage.UPLOAD);
    setFile(null);
    resumeIdRef.current = null;
    setResumeId(null);
    setBenchmarkDetails(null);
    setProgress(0);
    pushStageToUrl(BenchmarkStage.UPLOAD, null);
  }, [pushStageToUrl]);

  return (
    <BenchmarkContext.Provider
      value={{
        stage,
        file,
        resumeId,
        benchmarkDetails,
        progress,
        isStarting,
        startBenchmark,
        reset,
      }}
    >
      {children}
    </BenchmarkContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useBenchmarkContext() {
  const ctx = useContext(BenchmarkContext);
  if (!ctx) {
    throw new Error(
      "useBenchmarkContext must be used within a BenchmarkProvider",
    );
  }
  return ctx;
}
